import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Users, FileText, Shield, LogOut, Menu, X, Settings, User, HelpCircle, Sun, Moon } from 'lucide-react';
import { useAdmin } from '../contexts/AdminContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import { LanguageSwitcher } from './LanguageSwitcher';
import { Button } from './ui/button';
import { cn } from '../lib/utils';
import { useState } from 'react';

export const AdminLayout = ({ children }) => {
  const { admin, logout } = useAdmin();
  const { t } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const menuItems = [
    {
      path: '/admin/dashboard',
      icon: LayoutDashboard,
      label: t('admin.menu.dashboard'),
    },
    {
      path: '/admin/account-management',
      icon: Shield,
      label: 'Account Management',
    },
    {
      path: '/admin/logs',
      icon: FileText,
      label: t('admin.menu.activity_logs'),
    },
    {
      path: '/admin/settings',
      icon: Settings,
      label: t('admin.menu.settings'),
    },
  ];

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 transition-colors">
      {/* Sidebar - Desktop */}
      <aside className="fixed inset-y-0 left-0 z-50 w-64 bg-slate-900 dark:bg-slate-950 text-white hidden md:block border-r border-slate-800 dark:border-slate-900">
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-slate-800">
            <div className="space-y-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_id-selfservice/artifacts/tfwsm7zn_Logo%2BNuevo%2BMi-Token.png.webp"
                alt="Mi-Token Logo"
                className="h-10 w-auto"
              />
              <p className="text-xs text-slate-400 font-ibm">{t('admin.portal_title')}</p>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={cn(
                    'flex items-center gap-3 px-4 py-3 rounded-md font-ibm text-sm transition-colors',
                    isActive
                      ? 'bg-slate-800 text-white'
                      : 'text-slate-300 hover:bg-slate-800/50 hover:text-white'
                  )}
                >
                  <Icon className="h-5 w-5" />
                  {item.label}
                </Link>
              );
            })}
          </nav>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 bg-slate-900 text-white p-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <img 
            src="https://customer-assets.emergentagent.com/job_id-selfservice/artifacts/tfwsm7zn_Logo%2BNuevo%2BMi-Token.png.webp"
            alt="Mi-Token Logo"
            className="h-8 w-auto"
          />
        </div>
        <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
          {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-30 bg-slate-900 pt-16">
          <nav className="p-4 space-y-1">
            {menuItems.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-md text-white font-ibm"
                >
                  <Icon className="h-5 w-5" />
                  {item.label}
                </Link>
              );
            })}
            <div className="border-t border-slate-700 my-2"></div>
            <a
              href="https://mi-token.freshdesk.com/support/home"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 px-4 py-3 text-blue-400 font-ibm"
            >
              <HelpCircle className="h-5 w-5" />
              Support & Help
            </a>
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-4 py-3 text-white font-ibm w-full"
            >
              <LogOut className="h-5 w-5" />
              {t('admin.menu.logout')}
            </button>
          </nav>
        </div>
      )}

      {/* Top Header with User Info - Desktop */}
      <div className="hidden md:block fixed top-0 right-0 left-64 z-40 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 transition-colors">
        <div className="flex items-center justify-end px-6 py-4 gap-4">
          <a
            href="https://mi-token.freshdesk.com/support/home"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-3 py-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
            title="Support & Help"
          >
            <HelpCircle className="h-5 w-5" />
            <span className="text-sm font-ibm font-medium">Support</span>
          </a>
          <div className="h-6 w-px bg-slate-200 dark:bg-slate-700"></div>
          <button
            onClick={toggleTheme}
            className="flex items-center gap-2 px-3 py-2 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
            title={theme === 'light' ? 'Switch to Dark Mode' : 'Switch to Light Mode'}
          >
            {theme === 'light' ? (
              <>
                <Moon className="h-5 w-5" />
                <span className="text-sm font-ibm font-medium">Dark</span>
              </>
            ) : (
              <>
                <Sun className="h-5 w-5" />
                <span className="text-sm font-ibm font-medium">Light</span>
              </>
            )}
          </button>
          <div className="h-6 w-px bg-slate-200 dark:bg-slate-700"></div>
          <LanguageSwitcher />
          <div className="flex items-center gap-3 px-4 py-2 bg-slate-50 dark:bg-slate-800 rounded-lg">
            <User className="h-4 w-4 text-slate-600 dark:text-slate-400" />
            <div className="text-sm">
              <p className="font-medium text-slate-900 dark:text-slate-100">{admin?.username}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">{admin?.role?.replace('_', ' ')}</p>
            </div>
          </div>
          <Button
            onClick={handleLogout}
            variant="outline"
            size="sm"
            className="gap-2 text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100 dark:border-slate-700"
          >
            <LogOut className="h-4 w-4" />
            {t('admin.menu.logout')}
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="md:ml-64 min-h-screen bg-slate-200 dark:bg-slate-900">
        <div className="pt-16 md:pt-20">
          <main className="p-6">
            {children}
          </main>
        </div>
      </div>
    </div>
  );
};
